import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { withStyles } from '@material-ui/styles';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import StopSharpIcon from '@material-ui/icons/StopSharp';
import PlayCircleFilledWhiteOutlinedIcon from '@material-ui/icons/PlayCircleFilledWhiteOutlined';
import EditIcon from '@material-ui/icons/Edit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import GetAppIcon from '@material-ui/icons/GetApp';

import CurrentStatus from 'components/atoms/CurrentStatus';
import ToolTipWrap from 'components/molecules/ToolTipWrap';
import SearchInput from 'components/molecules/SearchInput';

const useToolbarStyles = makeStyles((theme) => ({
  tableToolbarArea: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 'auto',
    padding: '5px 10px',
    '& > div': {
      margin: '3px 7px 3px 0',
      '&:last-child': {
        margin: 0,
        marginLeft: 'auto',
      },
    },
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    lineHeight: '35px',
  },
  actions: {
    color: theme.palette.text.secondary,
    display: 'flex',
    marginLeft: 'auto',
  },
  iconBtn: {
    padding: 7,
  },
}));

const EnhancedTableToolbar = (props) => {
  const { t } = useTranslation();
  const classes = useToolbarStyles();
  const {
    numSelected,
    selectedVal,
    title,
    columnSetButton,
    hasExcel,
    screenMode,
    noFullScreen,
    handleExcelDonload,
    handleScreenMode,
    handleSearch,
    noneSearch,
    toolTip,
    isFrame,
    hasActController,
    headCallBack = (f) => f, // Head에서 사용하는 버튼 callBack함수
    loadingExcel = false,
  } = props;

  /**
   * 체크박스 Btn Callback 함수
   * @param type String
   * @param rowData Array
   */
  const handleHeadBtn = (type) => {
    headCallBack(type, selectedVal);
  };

  return (
    <Toolbar
      className={clsx(classes.tableToolbarArea, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {/* 툴팁 추가시 toolTip에 추가해주시면 됩니다. 여러줄 필요하실땐 변수에 담아서 <p>태그와 <br/>태그를 사용하여 추가해주세요. */}
      {toolTip !== null && toolTip !== undefined && (
        <>
          <ToolTipWrap
            className="tableFilter"
            title={toolTip}
            icon="faInfoCircle"
            iconColor="#c5c6d0"
          />
        </>
      )}
      {title !== '' && <div>{title}</div>}
      {/* 컬럼설정, 복사 등등 */}
      {columnSetButton}
      {/* 검색 */}
      {!noneSearch && (
        <SearchInput
          placeholder={t('검색어를 입력하세요.')}
          handleSearch={handleSearch}
        />
      )}
      {numSelected > 0 ? (
        <div>
          <Typography
            className={classes.title}
            color="inherit"
            variant="subtitle1"
          >
            {numSelected} {t('선택')}
          </Typography>
        </div>
      ) : (
        <div>
          <Typography
            className={classes.title}
            id="tableTitle"
            variant="subtitle1"
          />
        </div>
      )}
      <div className={classes.actions}>
        {numSelected > 0 ? (
          <>
            {hasActController.start === true ? (
              <ToolTipWrap title={t('게재')}>
                <IconButton
                  aria-label={t('게재')}
                  onClick={(e) => handleHeadBtn('start', [])}
                >
                  <PlayCircleFilledWhiteOutlinedIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
            {hasActController.stop === true ? (
              <ToolTipWrap title={t('중지')}>
                <IconButton
                  aria-label={t('중지')}
                  onClick={(e) => handleHeadBtn('stop', [])}
                >
                  <StopSharpIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
            {hasActController.reject === true ? (
              <ToolTipWrap title={t('반려')}>
                <IconButton
                  aria-label={t('반려')}
                  onClick={(e) => handleHeadBtn('reject', [])}
                >
                  <StopSharpIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
            {hasActController.waiting === true ? (
              <ToolTipWrap title={t('대기')}>
                <IconButton
                  aria-label={t('대기')}
                  onClick={(e) => handleHeadBtn('waiting', [])}
                >
                  <PlayCircleFilledWhiteOutlinedIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
            {hasActController.delete === true ? (
              <ToolTipWrap title={t('삭제')}>
                <IconButton
                  aria-label={t('삭제')}
                  onClick={(e) => handleHeadBtn('delete', [])}
                >
                  <DeleteIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
            {hasActController.edit === true ? (
              <ToolTipWrap title={t('수정')}>
                <IconButton
                  aria-label={t('수정')}
                  onClick={(e) => handleHeadBtn('edit', [])}
                >
                  <EditIcon />
                </IconButton>
              </ToolTipWrap>
            ) : null}
          </>
        ) : (
          <>
            {hasExcel && (
              <ToolTipWrap title={t('엑셀 다운로드')}>
                <IconButton
                  aria-label={t('엑셀 다운로드')}
                  onClick={loadingExcel ? (f) => f : handleExcelDonload}
                  className={classes.iconBtn}
                >
                  {loadingExcel ? (
                    <CurrentStatus type="excel" />
                  ) : (
                    <GetAppIcon />
                  )}
                </IconButton>
              </ToolTipWrap>
            )}
            {noFullScreen === 'noFullScreen' || !!isFrame ? null : (
              <IconButton
                aria-label={screenMode === '' ? t('전체 화면') : t('부분 화면')}
                onClick={handleScreenMode}
                className={classes.iconBtn}
              >
                {screenMode === '' ? (
                  <FullscreenIcon />
                ) : (
                  <FullscreenExitIcon />
                )}
              </IconButton>
            )}
          </>
        )}
      </div>
    </Toolbar>
  );
};

const useStyles = (theme) => ({
  root: {
    width: '100%',
    borderTop: theme.palette.box.border,
  },
  columnSet: (props) => {
    const { headcells } = props;
    const result = {};

    if (typeof headcells === 'undefined') {
      return null;
    }
    for (let headCell of headcells.values()) {
      const { id, className, displayFlag } = headCell;
      if (id === 'SEQ') {
        continue;
      }
      if (displayFlag) {
        // 오류있으면 김재형 대리에게 알려주세요
        // result[`& .${className}`] = {
        //   display: 'table-cell',
        // };
      } else {
        result[`& .${className}`] = {
          display: 'none',
        };
      }
    }
    return result;
  },
});

export default withStyles(useStyles)(
  class EnhancedTableWrap extends React.Component {
    render() {
      const {
        className = '',
        classes,
        selectedCnt,
        selectedVal,
        columnSetButton,
        title = '',
        hasExcel,
        screenMode,
        handleExcelDonload,
        handleScreenMode,
        children,
        handleSearch,
        noneSearch,
        noFullScreen,
        toolTip,
        isFrame,
        hasActController,
        headCallBack = (f) => f, // Head에서 사용하는 버튼 callBack함수
        loadingExcel,
        useToolbar = true, // 툴바사용여부
        ...rest
      } = this.props;

      return (
        <>
          <div
            className={clsx(classes.root, classes.columnSet, className)}
            {...rest}
          >
            {useToolbar === true ? (
              <EnhancedTableToolbar
                numSelected={selectedCnt}
                selectedVal={selectedVal}
                title={title}
                columnSetButton={columnSetButton}
                hasExcel={hasExcel}
                screenMode={screenMode}
                handleExcelDonload={handleExcelDonload}
                handleScreenMode={handleScreenMode}
                handleSearch={handleSearch}
                noneSearch={noneSearch}
                noFullScreen={noFullScreen}
                toolTip={toolTip}
                isFrame={isFrame}
                hasActController={hasActController}
                headCallBack={headCallBack}
                loadingExcel={loadingExcel}
              />
            ) : null}
            {children}
          </div>
        </>
      );
    }
  }
);
